import React from 'react';
import { Link, NavLink } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { capitalize, upperCase } from 'lodash';

import { useReservationLimitInPeriodContext } from '../../contexts/ReservationsLimitInPeriodContext';
import { Plan } from '../../pages/SubscriptionPlans/types';
import Button from '../Button/Button';
import { PlusIcon } from '../Dialogs/CollabsFilters/ToolTip';

type Props = {
  totalReservations: number;
  plan: Plan;
  limit?: number;
  fitContent?: boolean;
};

export const BusinessMonthlyReservationLimit: React.FC<Props> = ({
  totalReservations = 0,
  plan,
  limit,
  fitContent = false,
}) => {
  const reservationLimitContext = useReservationLimitInPeriodContext();
  const periodEndDate = reservationLimitContext?.periodEndDate;

  const planLabel = plan === 'plus' ? upperCase(plan) : capitalize(plan || 'free');
  const showUpgradeButton = !!limit && totalReservations >= limit && plan !== 'plus';

  return (
    <Container fitContent={fitContent && !showUpgradeButton}>
      <Box display={'flex'} alignItems={'center'} gap={4.5}>
        <Limit>
          {limit && totalReservations > limit ? limit : totalReservations}
          {plan !== 'plus' && !!limit && `/${limit}`}
        </Limit>
        <Box>
          <Typography sx={{ fontSize: '18px', color: '#232832', fontWeight: 600 }}>
            Monthly Promotions
          </Typography>
          <Typography sx={{ color: '#1A1C1E', fontSize: '15px' }}>
            {plan === 'plus' ? (
              <>
                Unlimited promotions included in your{' '}
                <b>
                  <NavLink style={{ color: '#1A1C1E', textDecoration: 'underline' }} to={'/plans'}>
                    {planLabel} Plan
                  </NavLink>
                </b>
                . Resets on {dayjs(periodEndDate).format('MMMM D, YYYY')}.
              </>
            ) : limit && totalReservations >= limit ? (
              <>
                Update your plan to unlock more promotions or wait until{' '}
                {dayjs(periodEndDate).format('MMMM D, YYYY')}.
              </>
            ) : (
              <>
                Included in your{' '}
                <b>
                  <NavLink style={{ color: '#1A1C1E', textDecoration: 'underline' }} to={'/plans'}>
                    {planLabel} Plan
                  </NavLink>
                </b>
                . Resets on {dayjs(periodEndDate).format('MMMM D, YYYY')}.
              </>
            )}
          </Typography>
        </Box>
      </Box>
      {showUpgradeButton && (
        <Button
          component={Link}
          variant='contained'
          color='primary'
          sx={{ fontSize: '18px', fontWeight: 500, px: 3, py: 1.3 }}
          startIcon={<PlusIcon sx={{ height: '16px', width: '16px' }} />}
          to='/plans'
        >
          Upgrade plan
        </Button>
      )}
    </Container>
  );
};

const Container = styled.div<{ fitContent?: boolean }>`
  border-radius: 8px;
  border: 1px solid #f3f3f3;
  background-color: #fafafa;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 22px 28px;
  width: ${({ fitContent }) => (fitContent ? 'fit-content' : '100%')};
`;

const Limit = styled.h1`
  color: #232832;
  font-family: Poppins;
  font-size: 40px;
  font-weight: 600;
  margin: 0;
  padding: 0;
`;
